import { observer } from 'mobx-react-lite';
import { useMainContext } from '../../hooks/useMainContext';
import { useScroll } from '../../hooks/useScroll';
import { DrawingBase } from './DrawingBase';
import { Closet } from './Closet';
import { PageViewer } from './PageViewer';
import { ButtonMode, ViewerMode } from '../../config';
import { ClosetMode } from '../../config';
import { Spin } from 'antd';
import { DoorForm } from '../DoorForm/DoorForm';
import { ObstacleForm } from '../ObstacleForm/ObstacleForm';
import { UnitForm } from '../UnitForm/UnitForm';
import { ClosetForm } from '../ClosetForm/ClosetForm';
import { LoadingOutlined } from '@ant-design/icons';
import { EditObstaclePosForm } from '../ObstacleForm/EditObstaclePosForm';
import { Loader } from './Loader';
import { useState, useCallback, useEffect, useRef } from 'react';
import { message, Modal } from 'antd';
import { InternalWallForm } from './InternalWallForms/InternalWallForm';

// Updated useClick hook
const useClick = ({
    onPointerDown,
    onPointerUp,
    onPointerMove,
    onRightMouseDown,
    onRightMouseUp,
    onRightMouseMove,
    onRightMouseLeave,
    onLeftMouseLeave,
    onDoubleClick,
    onPointerEnter,
    onClick,
}) => {
    const clickTimeout = useRef(null);
    const isDoubleClick = useRef(false);

    useEffect(() => {
        const preventContextMenu = (e) => e.preventDefault();
        document.addEventListener('contextmenu', preventContextMenu);
        return () => {
            document.removeEventListener('contextmenu', preventContextMenu);
        };
    }, []);

    const listeners = {
        onPointerDown: useCallback(
            (e) => {
                if (e.button === 2) {
                    e.preventDefault();
                    onRightMouseDown?.(e);
                } else {
                    onPointerDown?.(e);
                }
            },
            [onPointerDown, onRightMouseDown],
        ),
        onPointerEnter: useCallback(
            (e) => {
                onPointerEnter?.(e);
            },
            [onPointerEnter],
        ),
        onPointerUp: useCallback(
            (e) => {
                if (e.button === 2) {
                    e.preventDefault();
                    onRightMouseUp?.(e);
                } else {
                    onPointerUp?.(e);
                    if (onClick && e.button === 0) {
                        isDoubleClick.current = false;
                        clickTimeout.current = setTimeout(() => {
                            if (!isDoubleClick.current) {
                                onClick(e);
                            }
                        }, 300); // 300ms delay to differentiate from double-click
                    }
                }
            },
            [onPointerUp, onRightMouseUp, onClick],
        ),
        onPointerMove: useCallback(
            (e) => {
                if (e.buttons === 2) {
                    e.preventDefault();
                    onRightMouseMove?.(e);
                } else {
                    onPointerMove?.(e);
                }
            },
            [onPointerMove, onRightMouseMove],
        ),
        onPointerLeave: useCallback(
            (e) => {
                if (e.button === 2) {
                    e.preventDefault();
                    onRightMouseLeave?.(e);
                }
                if (e.button === -1) {
                    e.preventDefault();
                    onLeftMouseLeave?.(e);
                }
            },
            [onRightMouseLeave, onLeftMouseLeave],
        ),
        onDoubleClick: useCallback(
            (e) => {
                e.preventDefault();
                isDoubleClick.current = true; // Mark as double-click
                clearTimeout(clickTimeout.current); // Cancel single-click handler
                onDoubleClick?.(e);
            },
            [onDoubleClick],
        ),
    };

    return { listeners };
};

export const CanvasComponent = observer(() => {
    const {
        closetManager,
        drawingManager,
        viewManager,
        matrixManager,
        apiManager,
        operationsManager,
    } = useMainContext();

    const [isPanning, setIsPanning] = useState(false);
    const [startDrag, setStartDrag] = useState(null);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const [pointerDownTime, setPointerDownTime] = useState(null);
    const [showSaveReminder, setShowSaveReminder] = useState(false);

    // Timer for save reminder
    useEffect(() => {
        let intervalId;
        if (viewManager.selectedMenu === 2) {
            intervalId = setInterval(() => {
                setShowSaveReminder(true);
            }, 60 * 1000); // 1 minute
        }

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [viewManager.selectedMenu]);

    const { listeners: scrollListeners } = useScroll({
        onScrollUp: (e) => {
            if (!closetManager.enableZoom && !matrixManager.enableZoom) return;
            if (viewManager.selectedMenu === 3)
                matrixManager.zoomIn(e.clientX, e.clientY);
            if (viewManager.selectedMenu === 2)
                closetManager.zoomIn(e.clientX, e.clientY);
        },
        onScrollDown: (e) => {
            if (!closetManager.enableZoom && !matrixManager.enableZoom) return;
            if (viewManager.selectedMenu === 3)
                matrixManager.zoomOut(e.clientX, e.clientY);
            if (viewManager.selectedMenu === 2)
                closetManager.zoomOut(e.clientX, e.clientY);
        },
    });

    const { listeners: mouseEventListeners } = useClick({
        onPointerUp: () => {
            if (
                drawingManager.isDrawingModeOn &&
                viewManager.selectedMenu == 2
            ) {
                if (viewManager.buttonMode === ButtonMode.Closet) {
                    if (
                        closetManager.closetIntersectionForIncompleteClosets(
                            drawingManager.closetPolygon,
                        )
                    ) {
                        drawingManager.revertClosetPolygon();
                    }
                }
                if (viewManager.buttonMode === ButtonMode.Unit) {
                    if (
                        closetManager.unitIntersectionForIncompleteUnits(
                            drawingManager.closetPolygon,
                            1,
                        )
                    ) {
                        drawingManager.revertClosetPolygon();
                    }
                }
                drawingManager.checkIsSelfIntersecting();
            }
            if (
                closetManager.closetMode === ClosetMode.EDIT &&
                viewManager.selectedMenu == 2
            ) {
                if (closetManager.canObstacleMove) {
                    closetManager.setCanObstacleMove(false);
                    closetManager.setSelectedObstacle(null);
                }
                if (closetManager.canInternalWallMove) {
                    closetManager.setInternalWallMove(false);
                    closetManager.setInternalWallData(null);
                }
                closetManager.checkIsOutsideUnit();
                closetManager.setSelectedCloset(null);
                closetManager.setLastEditedPointIndex(null);
            }
            if (!closetManager.enableZoom && closetManager.panMode) {
                setIsPanning(false);
                closetManager.divRef.style.cursor = 'grab';
            }
            if (closetManager.initialSelectedCoords) {
                closetManager.setSelectedCloset(null);
                closetManager.setInitialSelectedCoords(null);
            }
        },
        onPointerEnter: (e) => {
            // Check mouse key is pressed or not
            if (e.buttons === 2) {
                return;
            }
            if (viewManager.selectedMenu == 2) {
                setIsPanning(false);
                closetManager.setPanMode(false);
                closetManager.setZoom(true);
                closetManager.divRef.style.cursor = 'auto';
            } else if (viewManager.selectedMenu == 3) {
                setIsPanning(false);
                matrixManager.setPanMode(false);
                matrixManager.setZoom(true);
                if (!matrixManager.divRef)
                    matrixManager.setDivRef(closetManager.divRef);
                matrixManager.divRef.style.cursor = 'auto';
            }
        },

        onPointerDown: (e) => {
            setPointerDownTime(Date.now());
            if (
                drawingManager.isInternalWallModeOn &&
                viewManager.selectedMenu == 2
            ) {
                drawingManager.addPointToClosetPolygonForInternalWall();
                return;
            }
            if (
                closetManager.closetMode === ClosetMode.EDIT &&
                viewManager.selectedMenu == 2
            ) {
                if (
                    closetManager.isObstacleNear({
                        x: e.clientX,
                        y: e.clientY,
                    })
                ) {
                    const { obstacle, isObstacle } =
                        closetManager.isObstacleNear({
                            x: e.clientX,
                            y: e.clientY,
                        });
                    if (obstacle && isObstacle) {
                        closetManager.setCanObstacleMove(true);
                        closetManager.setSelectedObstacle(obstacle);
                    } else if (obstacle && !isObstacle) {
                        closetManager.setInternalWallMove(true);
                        closetManager.setInternalWallData(obstacle);
                    }
                }
            }
            if (viewManager.isDropDownOpen) {
                viewManager.setIsDropDownOpen(false);
            }
            if (
                !closetManager.enableZoom &&
                closetManager.panMode &&
                viewManager.selectedMenu == 2
            ) {
                setStartDrag({ x: e.clientX, y: e.clientY });
                setIsPanning(true);
            }

            if (
                !matrixManager.enableZoom &&
                matrixManager.panMode &&
                viewManager.selectedMenu == 3
            ) {
                setStartDrag({ x: e.clientX, y: e.clientY });
                setIsPanning(true);
            }

            if (
                drawingManager.isCalibrationModeOn &&
                viewManager.selectedMenu == 2
            ) {
                drawingManager.addPointToClosetPolygonForCalibration({
                    x: e.clientX,
                    y: e.clientY,
                });
            }

            if (drawingManager.isUnitModeOn && viewManager.selectedMenu == 2) {
                drawingManager.addPointToClosetPolygonForUnit({
                    x: e.clientX,
                    y: e.clientY,
                });
                return;
            }
            if (drawingManager.isDoorModeOn && viewManager.selectedMenu == 2) {
                drawingManager.addPointToClosetPolygonForDoor({
                    x: e.clientX,
                    y: e.clientY,
                });
                return;
            }
            if (
                drawingManager.isRectangleModeOn &&
                viewManager.selectedMenu == 2 &&
                viewManager.buttonMode !== ButtonMode.Unit
            ) {
                drawingManager.addPointToRectanglePolygonForCloset({
                    x: e.clientX,
                    y: e.clientY,
                });
                return;
            }
            if (
                drawingManager.isRectangleModeOn &&
                viewManager.selectedMenu == 2 &&
                viewManager.buttonMode === ButtonMode.Unit
            ) {
                drawingManager.addPointToRectanglePolygonForUnit({
                    x: e.clientX,
                    y: e.clientY,
                });
                return;
            }
            if (
                drawingManager.isDrawingModeOn &&
                viewManager.selectedMenu == 2
            ) {
                drawingManager.addPointToClosetPolygon({
                    x: e.clientX,
                    y: e.clientY,
                });
                return;
            }

            if (
                closetManager.closetMode === ClosetMode.DELETE &&
                viewManager.selectedMenu == 2
            ) {
                const isDeleted = closetManager.deletePolygonPoint({
                    x: e.clientX,
                    y: e.clientY,
                });
                if (isDeleted) return;

                closetManager.deleteClosetPolygon({
                    x: e.clientX,
                    y: e.clientY,
                });
                return;
            }

            if (
                closetManager.closetMode === ClosetMode.OBSTACLE &&
                viewManager.selectedMenu == 2
            ) {
                closetManager.addObstacleAtPoint({
                    x: e.clientX,
                    y: e.clientY,
                });
            }

            if (
                closetManager.closetMode === ClosetMode.EDIT &&
                viewManager.selectedMenu == 2
            ) {
                closetManager.enableEditPolygon({
                    x: e.clientX,
                    y: e.clientY,
                });
                closetManager.addMidPoint({
                    x: e.clientX,
                    y: e.clientY,
                });
            }
        },

        onPointerMove: (e) => {
            setMousePosition({ x: e.clientX, y: e.clientY });
            if (
                drawingManager.isCalibrationModeOn &&
                viewManager.selectedMenu == 2
            ) {
                drawingManager.drawLivePointForCalibration({
                    x: e.clientX,
                    y: e.clientY,
                });
            }
            if (
                (drawingManager.isDrawingModeOn ||
                    drawingManager.isRectangleModeOn) &&
                !drawingManager.liveLine &&
                viewManager.selectedMenu == 2
            ) {
                drawingManager.drawLivePoint({
                    x: e.clientX,
                    y: e.clientY,
                });
            }
            if (
                drawingManager.isRectangleModeOn &&
                viewManager.selectedMenu == 2
            ) {
                drawingManager.drawLiveRectangle({
                    x: e.clientX,
                    y: e.clientY,
                });
                return;
            }
            if (
                closetManager.closetMode === ClosetMode.EDIT &&
                closetManager.selectedObstacle &&
                viewManager.selectedMenu == 2 &&
                closetManager.canObstacleMove
            ) {
                closetManager.updateObstaclePositionWithValidation({
                    x: e.clientX,
                    y: e.clientY,
                });
                return;
            }
            if (
                drawingManager.liveLine &&
                !drawingManager.isDoorModeOn &&
                viewManager.selectedMenu == 2
            ) {
                drawingManager.drawLiveLine({
                    x: e.clientX,
                    y: e.clientY,
                });
                return;
            }
            if (drawingManager.isDoorModeOn && viewManager.selectedMenu == 2) {
                drawingManager.drawLivePointForAttachments({
                    x: e.clientX,
                    y: e.clientY,
                });
            }
            if (
                closetManager.closetMode === ClosetMode.EDIT &&
                closetManager.selectedCloset &&
                viewManager.selectedMenu == 2
            ) {
                closetManager.updateSelectedClosetPolygon({
                    x: e.clientX,
                    y: e.clientY,
                });
                return;
            }
            if (
                drawingManager.isInternalWallModeOn &&
                viewManager.selectedMenu == 2
            ) {
                drawingManager.drawLiveLineForInternalWall({
                    x: e.clientX,
                    y: e.clientY,
                });
            }
            if (
                closetManager.closetMode === ClosetMode.EDIT &&
                closetManager.internalWallData &&
                viewManager.selectedMenu == 2 &&
                closetManager.canInternalWallMove
            ) {
                closetManager.updateInternalWallPositionWithValidation({
                    x: e.clientX,
                    y: e.clientY,
                });
            }
        },
        onDoubleClick: (e) => {
            if (
                closetManager.closetMode === ClosetMode.EDIT &&
                viewManager.selectedMenu == 2 &&
                closetManager.canObstacleMove
            ) {
                closetManager.setCanObstacleMove(false);
                closetManager.setSelectedObstacle(null);
                closetManager.updateObstaclePositionWithValidation({
                    x: e.clientX,
                    y: e.clientY,
                });
                return;
            }
            if (
                closetManager.closetMode === ClosetMode.EDIT &&
                viewManager.selectedMenu == 2 &&
                drawingManager.closetPolygon.length == 0
            ) {
                const attachmentData = closetManager.getAttachmentData({
                    x: e.clientX,
                    y: e.clientY,
                });
                if (attachmentData) {
                    closetManager.setAttachmentEditMode(true);
                }
            }
        },

        onRightMouseDown: (e) => {
            if (
                closetManager.closetMode === ClosetMode.EDIT &&
                viewManager.selectedMenu == 2
            ) {
                closetManager.enableEditObstacle({
                    x: e.clientX,
                    y: e.clientY,
                });
            }
            if (viewManager.selectedMenu == 2) {
                closetManager.setPanMode(true);
                closetManager.stopZoom();
                setIsPanning(true);
                closetManager.setStart(e.clientX, e.clientY);
                closetManager.setLast(
                    closetManager.translateX,
                    closetManager.translateY,
                );
            }
            if (viewManager.selectedMenu == 3) {
                matrixManager.setPanMode(true);
                matrixManager.stopZoom();
                setIsPanning(true);
                matrixManager.setStart(e.clientX, e.clientY);
                matrixManager.setLast(
                    matrixManager.translateX,
                    matrixManager.translateY,
                );
            }
        },

        onRightMouseUp: () => {
            if (viewManager.selectedMenu == 2) {
                setIsPanning(false);
                closetManager.setPanMode(false);
                closetManager.stopZoom();
            }
            if (viewManager.selectedMenu == 3) {
                setIsPanning(false);
                matrixManager.setPanMode(false);
                matrixManager.stopZoom();
            }
        },

        onRightMouseMove: (e) => {
            if (
                isPanning &&
                closetManager.panMode &&
                viewManager.selectedMenu == 2
            ) {
                closetManager.calculatePanRendering(e.clientX, e.clientY);
            }
            if (
                isPanning &&
                matrixManager.panMode &&
                viewManager.selectedMenu == 3
            ) {
                matrixManager.calculatePanRendering(e.clientX, e.clientY);
            }
        },

        onRightMouseLeave: () => {
            if (viewManager.selectedMenu == 2) {
                setIsPanning(false);
                closetManager.setPanMode(false);
                if (closetManager.selectedCloset)
                    closetManager.setSelectedCloset(null);
                closetManager.setSelectedAttachment(null);
                closetManager.stopZoom();
            }
            if (viewManager.selectedMenu == 3) {
                setIsPanning(false);
                matrixManager.setPanMode(false);
                if (closetManager.selectedCloset)
                    closetManager.setSelectedCloset(null);
                closetManager.setSelectedAttachment(null);
                matrixManager.stopZoom();
            }
        },
        onLeftMouseLeave: () => {
            if (!viewManager.closetForm) {
                closetManager.checkIsOutsideUnit();
                if (closetManager.selectedCloset)
                    closetManager.setSelectedCloset(null);
            }
        },
        onClick: (e) => {
            const clickDuration = Date.now() - pointerDownTime;
            if (clickDuration > 700) {
                return;
            }

            if (
                closetManager.closetMode === ClosetMode.EDIT &&
                viewManager.selectedMenu == 2
            ) {
                const obstacleData =
                    closetManager.getObstacleAndInternalWallData({
                        x: e.clientX,
                        y: e.clientY,
                    });
                if (obstacleData) {
                    closetManager.setObstacleEditMode(true);
                    return;
                }
                const InternalWallData =
                    closetManager.getObstacleAndInternalWallData(
                        {
                            x: e.clientX,
                            y: e.clientY,
                        },
                        false,
                    );
                if (InternalWallData) {
                    closetManager.setInternalForm(true);
                    return;
                }
            }
        },
    });

    const handleSave = async () => {
        closetManager.setIsSaving(true);
        try {
            await operationsManager.downloadClosetData();
        } catch (error) {
            console.error('Failed to save changes:', error);
            message.error('Failed to save changes.');
        } finally {
            closetManager.setIsSaving(false);
        }
        setShowSaveReminder(false);
    };

    const handleCancelReminder = () => {
        setShowSaveReminder(false);
    };

    const handleKeyDownEvent = (e) => {
        switch (e.key) {
            case 'Escape':
                drawingManager.stopDrawing();
                break;
            case 'Shift':
                drawingManager.setShouldSnap(false);
                break;
            case 'z':
                if (e.ctrlKey && drawingManager.closetPolygon.length > 0) {
                    drawingManager.revertClosetPolygon(1);
                    drawingManager.drawLiveLine({
                        x: mousePosition.x,
                        y: mousePosition.y,
                    });
                }
                break;
            default:
                break;
        }
    };

    const handleKeyUpEvent = (e) => {
        switch (e.key) {
            case 'Shift':
                drawingManager.setShouldSnap(true);
                break;
            default:
                break;
        }
    };

    const handleCanvasDimensions = () => {
        if (viewManager.selectedMenu == 2) {
            return 'w-full h-[calc(100vh-80px)]';
        } else {
            return 'w-full h-[calc(100vh-80px)]';
        }
    };

    return (
        <>
            {viewManager.isLoading ? (
                <div className="h-[calc(100vh-56px)] flex justify-center items-center">
                    <Spin
                        indicator={
                            <LoadingOutlined
                                style={{ fontSize: 48, color: '#f46147' }}
                                spin
                            />
                        }
                    />
                </div>
            ) : (
                <>
                    {closetManager.isLoading || matrixManager.isLoading ? (
                        <div className="h-[calc(100vh-56px)] flex justify-center items-center">
                            <Loader />
                        </div>
                    ) : (
                        <>
                            <DoorForm />
                            <ObstacleForm />
                            <InternalWallForm />
                            <UnitForm />
                            <ClosetForm />
                            <EditObstaclePosForm />
                        </>
                    )}

                    <div
                        ref={(ref) => ref && closetManager.setDivRef(ref)}
                        {...scrollListeners}
                        {...mouseEventListeners}
                        tabIndex={0}
                        className={`${handleCanvasDimensions()} ${closetManager.closetCss} relative`}
                        style={{
                            cursor:
                                viewManager.selectedMenu == 2
                                    ? !closetManager.enableZoom
                                        ? 'grab'
                                        : 'default'
                                    : !matrixManager.enableZoom
                                      ? 'grab'
                                      : 'default',
                        }}
                        onKeyDown={handleKeyDownEvent}
                        onKeyUp={handleKeyUpEvent}>
                        <PageViewer
                            viewerMode={
                                viewManager.selectedMenu === 2
                                    ? ViewerMode.ANNOTATE
                                    : ViewerMode.MATRIX
                            }
                        />
                        <DrawingBase />
                        <Closet />
                        <div
                            className={`save-reminder-container ${showSaveReminder && viewManager.superAdmin ? 'visible' : ''}`}
                            style={{
                                position: 'absolute',
                                bottom: '20px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                color: 'white',
                                padding: '15px 20px',
                                borderRadius: '8px',
                                zIndex: '1000',
                                display:
                                    showSaveReminder && viewManager.superAdmin
                                        ? 'flex'
                                        : 'none',
                                alignItems: 'center',
                                gap: '20px',
                            }}>
                            <span style={{ fontSize: '16px' }}>
                                Your changes haven&apos;t been saved in a while
                            </span>
                            <button
                                disabled={!viewManager.superAdmin}
                                onClick={handleSave}
                                style={{
                                    backgroundColor: viewManager.superAdmin
                                        ? '#4CAF50'
                                        : '#666',
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 15px',
                                    borderRadius: '5px',
                                    cursor: viewManager.superAdmin
                                        ? 'pointer'
                                        : 'not-allowed',
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                }}>
                                Save
                            </button>
                            <button
                                disabled={!viewManager.superAdmin}
                                onClick={handleCancelReminder}
                                style={{
                                    backgroundColor: viewManager.superAdmin
                                        ? '#f44336'
                                        : '#666',
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 15px',
                                    borderRadius: '5px',
                                    cursor: viewManager.superAdmin
                                        ? 'pointer'
                                        : 'not-allowed',
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                }}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </>
            )}
        </>
    );
});
